import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateLLMModelSpecificationRequest, LLMModelSpecification, QueryKey } from 'api/model.ts';
import { toast } from 'react-toastify';
import { getModelSpecification, updateModelSpecification } from './requests';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.ModelSpecifications;

type Props = {
  name: string;
  provider: string;
  modelSpecification?: LLMModelSpecification;
};

const useModelSpecification = ({ name, provider, modelSpecification: initialModelSpecification }: Props) => {
  const queryClient = useQueryClient();

  const { data: latestModelSpecification, isLoading } = useQuery<LLMModelSpecification, Error>({
    queryKey: [queryKey, { name, provider }],
    queryFn: () => getModelSpecification({ name, provider }),
    enabled: !!name && !!provider && !initialModelSpecification,
  });

  const modelSpecification = latestModelSpecification ?? initialModelSpecification;

  const update = useMutation({
    mutationFn: (modelSpecification: CreateLLMModelSpecificationRequest) =>
      updateModelSpecification({ name, provider, model: modelSpecification }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model specification successfully updated');
    },
    onError: errorHandler('Updating model specification failed'),
  });

  const clearCache = () => {
    void queryClient.invalidateQueries({ queryKey: [queryKey, { name, provider }] });
  };

  return {
    modelSpecification,
    isLoading,
    update,
    clearCache,
  };
};

export default useModelSpecification;
